import { Pencil } from 'lucide-react';
import { cn } from '@/lib/utils';
import { SALES_REP_ROLE_LABEL, VERTICAL_LABEL, formatGhs, type SalesRep } from '@/client/types/sales';
import { initials } from '../sales-ui';

const ROLE_TONE: Record<SalesRep['role'], string> = {
  manager: 'border-violet-200 bg-violet-50 text-violet-800',
  lead: 'border-sky-200 bg-sky-50 text-sky-800',
  agent: 'border-gray-200 bg-gray-50 text-gray-700',
};

interface RepCardProps {
  rep: SalesRep;
  cap: number;
  isYou: boolean;
  canManage: boolean;
  onEdit: () => void;
  onToggleActive: (active: boolean) => void;
}

export function RepCard({ rep, cap, isYou, canManage, onEdit, onToggleActive }: RepCardProps) {
  const active = rep.active !== false;
  return (
    <article
      className={cn(
        'flex flex-col rounded-xl border border-gray-200 bg-white p-4 transition-shadow hover:shadow-sm',
        !active && 'opacity-60',
      )}
    >
      <div className="flex items-start gap-3">
        <span
          className={cn(
            'flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white',
            rep.avatarColor,
          )}
        >
          {initials(rep.name)}
        </span>
        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-1.5 truncate text-sm font-semibold text-gray-900">
            <span className="truncate">{rep.name}</span>
            {isYou && (
              <span className="rounded-full bg-slate-900 px-1.5 py-px text-[10px] font-medium text-white">You</span>
            )}
          </p>
          <p className="truncate text-xs text-gray-500">{rep.title || rep.email || '—'}</p>
        </div>
        {canManage && (
          <button
            type="button"
            onClick={onEdit}
            aria-label={`Edit ${rep.name}`}
            className="rounded-md p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      <div className="mt-3 flex flex-wrap gap-1.5">
        <span className={cn('rounded-full border px-2 py-0.5 text-[11px] font-medium', ROLE_TONE[rep.role])}>
          {SALES_REP_ROLE_LABEL[rep.role]}
        </span>
        <span className="rounded-full border border-gray-200 px-2 py-0.5 text-[11px] font-medium text-gray-700">
          {rep.desk === 'all' ? 'All desks' : VERTICAL_LABEL[rep.desk]}
        </span>
        {!active && (
          <span className="rounded-full border border-gray-300 bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600">
            Inactive
          </span>
        )}
      </div>

      <div className="mt-4 flex items-center justify-between border-t border-gray-100 pt-3 text-xs text-gray-500">
        <span>
          Up to {cap} claims
          {rep.quotaGhs != null && <> · {formatGhs(rep.quotaGhs)} quota</>}
        </span>
        {canManage && (
          <label className="flex cursor-pointer items-center gap-2">
            <span>{active ? 'Active' : 'Off the floor'}</span>
            <button
              type="button"
              role="switch"
              aria-checked={active}
              aria-label={active ? `Take ${rep.name} off the floor` : `Put ${rep.name} back on the floor`}
              onClick={() => onToggleActive(!active)}
              className={cn(
                'relative h-5 w-9 rounded-full transition-colors',
                active ? 'bg-emerald-600' : 'bg-gray-300',
              )}
            >
              <span
                className={cn(
                  'absolute left-0 top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform',
                  active ? 'translate-x-4' : 'translate-x-0.5',
                )}
              />
            </button>
          </label>
        )}
      </div>
    </article>
  );
}
